import React, { useState } from 'react';
import MagicWand from '@uber/base-icons/magic-wand';
import { PLACEMENT, Popover } from 'baseui/popover';
import ToolbarButton, { TOOLBAR_BUTTON_SIZE } from '../../../../common/toolbar-button';
import type { AnnotationLabelT } from '../../../../common/types/types';
import { type AutoDetectModalPhaseT, type AutoDetectSegmentationPanelProps, AUTO_DETECT_MODAL_PHASE, AutoDetectSegmentationPanel } from './auto-detect-segmentation-panel';
import { getSAM3TextPrompt } from './utils';
const POPOVER_MARGIN = 24;
const POPOVER_MARGIN_WITH_TIGHTNESS_SLIDER = 68;
type AutoDetectToolbarButtonProps = {
  detect: AutoDetectSegmentationPanelProps['onDetect'];
  submitAll: () => void;
  cancelAll: () => void;
  isDetecting: boolean;
  detectedCount: number;
  selectedClassName: string;
  pendingAnnotationLabel: AnnotationLabelT | null;
  isSelected: boolean;
  disabled: boolean;
  testId: string;
  onSelect: () => void;
};
const AutoDetectToolbarButton = ({
  detect,
  submitAll,
  cancelAll,
  isDetecting,
  detectedCount,
  selectedClassName,
  pendingAnnotationLabel,
  isSelected,
  disabled,
  testId,
  onSelect
}: AutoDetectToolbarButtonProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [modalPhase, setModalPhase] = useState<AutoDetectModalPhaseT>(AUTO_DETECT_MODAL_PHASE.PROMPT);

  // Once a detection batch exists, keep showing the label it was run with,
  // not whatever is currently globally selected.
  const displayedClassName = pendingAnnotationLabel ? getSAM3TextPrompt(pendingAnnotationLabel) : selectedClassName;
  const resetAutoDetectPanel = () => {
    setIsPopoverOpen(false);
    setModalPhase(AUTO_DETECT_MODAL_PHASE.PROMPT);
  };
  const popoverMargin = modalPhase === AUTO_DETECT_MODAL_PHASE.RESULTS ? POPOVER_MARGIN_WITH_TIGHTNESS_SLIDER : POPOVER_MARGIN;
  return <Popover isOpen={isPopoverOpen} placement={PLACEMENT.bottomRight} popoverMargin={popoverMargin} onClickOutside={() => {
    if (modalPhase === AUTO_DETECT_MODAL_PHASE.RESULTS || isDetecting) return;
    resetAutoDetectPanel();
  }} onEsc={() => {
    if (isDetecting) return;
    if (modalPhase === AUTO_DETECT_MODAL_PHASE.PROMPT) {
      cancelAll();
    }
    resetAutoDetectPanel();
  }} overrides={{
    Body: {
      style: {
        boxShadow: 'none',
        backgroundColor: 'transparent'
      }
    },
    Inner: {
      style: {
        backgroundColor: 'transparent',
        padding: 0
      }
    }
  }} content={<AutoDetectSegmentationPanel isLoading={isDetecting} selectedClassName={displayedClassName} resultCount={detectedCount} modalPhase={modalPhase} onPhaseChange={setModalPhase} onClose={() => {
    cancelAll();
    resetAutoDetectPanel();
  }} onDetect={detect} onSubmitAll={() => {
    submitAll();
    resetAutoDetectPanel();
  }} onCancelAll={() => {
    cancelAll();
    resetAutoDetectPanel();
  }} />}>
      <ToolbarButton title="Auto-detect objects" onClick={() => {
      onSelect();
      setIsPopoverOpen(true);
    }} customStyles={{
      marginBottom: 0
    }} attributes={{
      'data-testid': testId
    }} isSelected={isSelected} size={TOOLBAR_BUTTON_SIZE.SMALL} disabled={disabled}>
        <MagicWand />
      </ToolbarButton>
    </Popover>;
};
export default AutoDetectToolbarButton;